import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Info, ChevronLeft, ChevronRight, Trees, Shrub, Sprout, Flower2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { nurseryProducts } from '../data/nurseryProducts';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';

// Product Image Carousel Component
const ProductImageCarousel = ({ images, name, captions, zoomOut, zoomOutImages }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const imageArray = Array.isArray(images) ? images : [images];
  const captionArray = Array.isArray(captions) ? captions : [];
  const zoomOutArray = zoomOutImages || [];
  
  const shouldZoomOut = zoomOut || zoomOutArray.includes(currentIndex);
  const objectFitClass = shouldZoomOut ? 'object-contain bg-gray-100' : 'object-cover';
  
  if (imageArray.length === 1) {
    return (
      <img 
        src={imageArray[0]}
        alt={name}
        className={`w-full h-full ${objectFitClass} hover:scale-110 transition-transform duration-500`}
      />
    );
  }

  return (
    <div className="relative w-full h-full">
      <img 
        src={imageArray[currentIndex]}
        alt={`${name} - ${currentIndex + 1}`}
        className={`w-full h-full ${objectFitClass} transition-transform duration-500`}
      />
      
      {/* Photo count badge */}
      <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
        <span>{currentIndex + 1}/{imageArray.length}</span>
      </div>
      
      {captionArray[currentIndex] && (
        <div className="absolute bottom-12 left-2 right-2 bg-black/60 text-white text-sm px-2 py-1 rounded text-center">
          {captionArray[currentIndex]}
        </div>
      )}
      
      {/* Always visible navigation arrows */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setCurrentIndex((prev) => (prev === 0 ? imageArray.length - 1 : prev - 1));
        }}
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-colors shadow-lg"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setCurrentIndex((prev) => (prev === imageArray.length - 1 ? 0 : prev + 1));
        }}
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-colors shadow-lg"
      >
        <ChevronRight size={20} />
      </button>
      
      {/* Dot indicators */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2">
        {imageArray.map((_, idx) => (
          <button
            key={idx}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex(idx);
            }}
            className={`w-3 h-3 rounded-full transition-all ${
              idx === currentIndex 
                ? 'bg-white scale-110' 
                : 'bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Horizontal Tree Card for Carousel
const TreeCard = ({ product, onInquire, badgeText = "Tree", badgeColor = "bg-emerald-700" }) => {
  return (
    <div className="flex-shrink-0 w-80 bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
      <div className="relative h-48 overflow-hidden">
        <ProductImageCarousel 
          images={product.image} 
          name={product.name} 
          captions={product.imageCaption} 
          zoomOut={product.zoomOut} 
          zoomOutImages={product.zoomOutImages} 
        />
        <Badge className={`absolute top-3 left-3 ${badgeColor} text-white`}>
          {badgeText}
        </Badge>
      </div>
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h4 className="text-lg font-bold text-black" style={{ fontFamily: "'Crimson Text', serif" }}>
            {product.name}
          </h4>
          <span className="text-xl font-bold text-emerald-700">${product.price.toFixed(2)}</span>
        </div>
        <p className="text-xs italic text-gray-500 mb-2">{product.scientificName}</p>
        <p className="text-sm text-gray-700 line-clamp-2 mb-3">{product.description}</p>
        <div className="grid grid-cols-2 gap-1 text-xs bg-gray-50 rounded-lg p-2 mb-3">
          <span className="text-gray-600">Size: <span className="font-medium">{product.size.split('/')[0]}</span></span>
          <span className="text-gray-600">Zones: <span className="font-medium">{product.growingZones}</span></span>
          <span className="text-gray-600">Height: <span className="font-medium">{product.matureHeight}</span></span>
          <span className="text-gray-600">Sun: <span className="font-medium">{product.sunRequirement.split(' ')[0]}</span></span>
        </div>
        <Button 
          onClick={() => onInquire(product)}
          className="w-full bg-emerald-700 text-white hover:bg-black text-sm py-2"
        >
          <ShoppingCart className="mr-2" size={16} />
          Add to Cart
        </Button>
      </div>
    </div>
  );
};

// Trees Horizontal Carousel Section
const TreesCarousel = ({ trees, onInquire }) => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-gradient-to-r from-emerald-900 to-emerald-700 py-10 px-4 sm:px-6 lg:px-8 mb-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Trees className="text-white" size={36} />
            <div>
              <h2 
                className="text-3xl font-bold text-white"
                style={{ fontFamily: "'Crimson Text', serif" }}
              >
                Trees Collection
              </h2>
              <p className="text-emerald-200 text-sm">{trees.length} varieties available</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => scroll('right')}
              className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
        
        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {trees.map((tree) => (
            <TreeCard key={tree.id} product={tree} onInquire={onInquire} badgeText="Tree" badgeColor="bg-emerald-700" />
          ))}
        </div>
      </div>
    </div>
  );
};

// Shrubs Horizontal Carousel Section
const ShrubsCarousel = ({ shrubs, onInquire }) => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-gradient-to-r from-amber-800 to-amber-600 py-10 px-4 sm:px-6 lg:px-8 mb-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shrub className="text-white" size={36} />
            <div>
              <h2 
                className="text-3xl font-bold text-white"
                style={{ fontFamily: "'Crimson Text', serif" }}
              >
                Shrubs Collection
              </h2>
              <p className="text-amber-200 text-sm">{shrubs.length} varieties available</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => scroll('right')}
              className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
        
        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {shrubs.map((shrub) => (
            <TreeCard key={shrub.id} product={shrub} onInquire={onInquire} badgeText="Shrub" badgeColor="bg-amber-700" />
          ))}
        </div>
      </div>
    </div>
  );
};

// Vegetable Transplants Horizontal Carousel Section
const TransplantsCarousel = ({ transplants, onInquire }) => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-gradient-to-r from-pink-700 to-rose-500 py-10 px-4 sm:px-6 lg:px-8 mb-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Sprout className="text-white" size={36} />
            <div>
              <h2 
                className="text-3xl font-bold text-white"
                style={{ fontFamily: "'Crimson Text', serif" }}
              >
                Spring Transplants
              </h2>
              <p className="text-pink-200 text-sm">{transplants.length} varieties available</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => scroll('right')}
              className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
        
        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {transplants.map((transplant) => (
            <TreeCard key={transplant.id} product={transplant} onInquire={onInquire} badgeText="Transplant" badgeColor="bg-pink-600" />
          ))}
        </div>
      </div>
    </div>
  );
};

// Perennials Horizontal Carousel Section
const PerennialsCarousel = ({ perennials, onInquire }) => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-gradient-to-r from-purple-800 to-violet-600 py-10 px-4 sm:px-6 lg:px-8 mb-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Flower2 className="text-white" size={36} />
            <div>
              <h2 
                className="text-3xl font-bold text-white"
                style={{ fontFamily: "'Crimson Text', serif" }}
              >
                Perennials Collection
              </h2>
              <p className="text-purple-200 text-sm">{perennials.length} varieties available</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => scroll('right')}
              className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>
        
        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {perennials.map((perennial) => (
            <TreeCard key={perennial.id} product={perennial} onInquire={onInquire} badgeText="Perennial" badgeColor="bg-purple-700" />
          ))}
        </div>
      </div>
    </div>
  );
};

const NurseryShop = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { addItem, itemCount, setIsCartOpen } = useCart();

  const categories = ['All', 'Hardy Tropical', 'Rare Houseplant', 'Fruit', 'Fruit Tree', 'Workshop'];

  // Separate trees, shrubs, transplants, and perennials from other products
  const treeProducts = nurseryProducts.filter(product => product.category === 'Tree');
  const shrubProducts = nurseryProducts.filter(product => product.category === 'Shrub');
  const transplantProducts = nurseryProducts.filter(product => product.category === 'Annual');
  const perennialProducts = nurseryProducts.filter(product => product.category === 'Perennial');
  const otherProducts = nurseryProducts.filter(product => 
    product.category !== 'Tree' && 
    product.category !== 'Shrub' && 
    product.category !== 'Annual' &&
    product.category !== 'Perennial'
  );

  const filteredProducts = selectedCategory === 'All' 
    ? otherProducts 
    : otherProducts.filter(product => product.category === selectedCategory);

  const handleAddToCart = (product) => {
    addItem(product, 1, product.size?.split('/')[0]?.trim() || null);
  };

  const categoryStyles = {
    'All': { bg: 'bg-black', text: 'text-white' },
    'Perennial': { bg: 'bg-green-600', text: 'text-white' },
    'Hardy Tropical': { bg: 'bg-orange-500', text: 'text-white' },
    'Rare Houseplant': { bg: 'bg-purple-600', text: 'text-white' },
    'Fruit': { bg: 'bg-red-500', text: 'text-white' },
    'Fruit Tree': { bg: 'bg-amber-600', text: 'text-white' },
    'Workshop': { bg: 'bg-indigo-600', text: 'text-white' }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Floating Cart Button */}
      <button
        onClick={() => setIsCartOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-emerald-700 hover:bg-emerald-800 text-white p-4 rounded-full shadow-lg transition-all hover:scale-105"
        data-testid="floating-cart-button"
      >
        <ShoppingCart size={24} />
        {itemCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
            {itemCount}
          </span>
        )}
      </button>

      {/* Hero Background */}
      <div 
        className="relative bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_sustainable-farm-5/artifacts/x06u2eoo_1000000442.jpg')`,
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-between items-start">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="mb-4 text-white hover:text-emerald-400 hover:bg-white/10"
            >
              <ArrowLeft className="mr-2" size={20} />
              Back to Home
            </Button>
            <Button
              onClick={() => setIsCartOpen(true)}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              data-testid="header-cart-button"
            >
              <ShoppingCart className="mr-2" size={20} />
              Cart {itemCount > 0 && `(${itemCount})`}
            </Button>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <ShoppingCart size={40} className="text-emerald-400" />
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Plant Nursery
            </h1>
          </div>
          <p className="text-lg text-gray-200 max-w-3xl">
            Browse our extensive collection of sustainably grown plants — from rare fruits and native perennials 
            to vegetable starts and magnificent trees. Everything propagated with care at AGreenerSide.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-sm text-gray-300">
            <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">🌱 {nurseryProducts.length}+ Varieties</span>
            <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">🌿 Sustainably Grown</span>
            <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">📍 Zone 7 Adapted</span>
          </div>
        </div>
      </div>

      {/* Trees Horizontal Carousel */}
      <TreesCarousel trees={treeProducts} onInquire={handleAddToCart} />

      {/* Shrubs Horizontal Carousel */}
      <ShrubsCarousel shrubs={shrubProducts} onInquire={handleAddToCart} />

      {/* Perennials Horizontal Carousel */}
      <PerennialsCarousel perennials={perennialProducts} onInquire={handleAddToCart} />

      {/* Spring Transplants Horizontal Carousel */}
      <TransplantsCarousel transplants={transplantProducts} onInquire={handleAddToCart} />

      {/* Category Filter */}
      <div className="sticky top-16 z-40 bg-white/95 backdrop-blur-sm shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Browse Other Categories</h3>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => {
              const style = categoryStyles[category] || { bg: 'bg-gray-500', text: 'text-white' };
              const isSelected = selectedCategory === category;
              return (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`transition-all duration-200 ${
                    isSelected 
                      ? `${style.bg} ${style.text} ring-2 ring-offset-2 ring-black scale-105` 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                  {isSelected && (
                    <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                      {filteredProducts.length}
                    </span>
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Product Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => {
            const categoryStyle = categoryStyles[product.category] || { bg: 'bg-gray-500' };
            return (
              <Card key={product.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 bg-white">
                <div className="relative h-64 overflow-hidden">
                  <ProductImageCarousel images={product.image} name={product.name} captions={product.imageCaption} zoomOut={product.zoomOut} zoomOutImages={product.zoomOutImages} />
                  <Badge className={`absolute top-4 left-4 ${categoryStyle.bg} text-white z-10`}>
                    {product.category}
                  </Badge>
                  {product.inStock && (
                    <Badge className="absolute top-4 right-4 bg-green-600 z-10">
                      In Stock
                    </Badge>
                  )}
                </div>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl" style={{ fontFamily: "'Crimson Text', serif" }}>
                        {product.name}
                      </CardTitle>
                      <CardDescription className="italic text-sm mt-1">
                        {product.scientificName}
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-black">${product.price.toFixed(2)}</span>
                      {product.size.includes('/') && (
                        <p className="text-xs text-gray-500">starting at</p>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-gray-700 mb-4 text-sm line-clamp-3">
                    {product.description}
                  </p>
                  <div className="space-y-1 text-xs bg-gray-50 rounded-lg p-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Size:</span>
                      <span className="font-medium text-right max-w-[60%]">{product.size}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Zones:</span>
                      <span className="font-medium">{product.growingZones}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sun:</span>
                      <span className="font-medium">{product.sunRequirement}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Height:</span>
                      <span className="font-medium">{product.matureHeight}</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    onClick={() => handleAddToCart(product)}
                    className="w-full bg-black text-white hover:bg-emerald-700 transition-all duration-200"
                  >
                    <ShoppingCart className="mr-2" size={18} />
                    Add to Cart
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-16 bg-white rounded-2xl p-8 text-center shadow-lg">
          <Info className="mx-auto mb-4 text-emerald-600" size={48} />
          <h3 
            className="text-2xl font-bold text-black mb-3"
            style={{ fontFamily: "'Crimson Text', serif" }}
          >
            Visit Us for Plant Selection
          </h3>
          <p className="text-gray-700 max-w-2xl mx-auto mb-6">
            These listings represent our typical inventory. Availability varies by season. 
            Contact us or visit the nursery to see our current selection and get expert planting advice.
          </p>
          <Button 
            onClick={() => {
              const element = document.getElementById('book-visit-cta');
              element?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-black text-white hover:bg-emerald-700"
          >
            Book a Visit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NurseryShop;
